/**
 * Os componentes do design system — §3 de `docs/10-design-system.md`.
 *
 * Ficam num arquivo só porque são pequenos e porque a coesão importa mais que a divisão:
 * quem mexe no badge geralmente mexe no que está ao lado dele. Quando algum crescer a
 * ponto de ter lógica própria, ele sai daqui.
 *
 * Nenhum deles aceita `className` livre com cor. O ponto de ter tokens é que a cor venha do
 * significado (`severidade`, `cor`), e não de quem estiver escrevendo a tela naquele dia.
 */
import { numeroLongo } from "../formato";

import type { ReactNode } from "react";

import { TINTA } from "./tinta";
import type { Significado } from "./tinta";

export type { Significado };

/**
 * §3: fundo tingido a 14%, borda a 40%, texto na cor cheia, caixa alta, 10px.
 *
 * Um badge por célula. E o texto vai sempre junto da cor (§6): estado comunicado só por
 * cor não chega em quem não distingue verde de âmbar.
 */
export function Badge({
  cor,
  children,
}: {
  cor: Significado;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-badge border px-1.5 py-0.5 text-badge font-bold tracking-[0.04em] uppercase ${TINTA[cor]}`}
    >
      {children}
    </span>
  );
}

/** A pílula de estado do sistema, na topbar. Redonda para não se confundir com badge. */
export function Pilula({
  cor,
  children,
}: {
  cor: Significado;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-pilula border px-2.5 py-1 text-[10.5px] font-semibold tracking-[0.03em] uppercase ${TINTA[cor]}`}
    >
      {children}
    </span>
  );
}

/**
 * §3: título em caixa alta à esquerda, ações à direita na mesma linha, corpo, rodapé.
 *
 * O corpo é o único que rola. A página inteira não rola de propósito: numa sala de
 * monitoramento, perder a régua de KPIs de vista porque alguém rolou a página é perder a
 * razão de a régua existir.
 */
export function Painel({
  titulo,
  acoes,
  rodape,
  children,
  rolagemPropria = false,
  janelaNoCelular = false,
}: {
  titulo: string;
  acoes?: ReactNode;
  rodape?: ReactNode;
  children: ReactNode;
  /** No celular, o painel é uma janela de 70% da tela que rola por dentro — para a lista
   *  longa da mesa (a fila da Operação) não empurrar o resto da tela para o fim da página.
   *  Sem isto, no celular o painel tem a altura do conteúdo e quem rola é a página. */
  janelaNoCelular?: boolean;
  /** O último filho rola sozinho, nos dois sentidos, e o resto fica parado em cima — para
   *  tabela larga (Gisele, 25/09: "a seta para a direita não vai, não dá para ver as outras
   *  colunas"). Com o corpo inteiro rolando, a barra horizontal da tabela ficava no fim das
   *  cinquenta linhas, fora de vista. Quem usa põe `min-h-0 overflow-auto` no último filho. */
  rolagemPropria?: boolean;
}) {
  return (
    // No celular, **a página rola, o painel não** (06/10, `C-17` fase 2). Antes, todo
    // painel era uma janela de `70dvh` com rolagem própria — e numa tela de 664 px de altura
    // útil a régua gerencial aparecia numa fresta de 378 px que rolava dentro de uma página
    // que também rolava, e a tabela da SENATRAN ficava com 63 px. Duas barras para o mesmo
    // gesto. Agora a janela é exceção declarada (`janelaNoCelular`), para a fila longa da
    // Operação. Na mesa nada muda: o teto é a própria moldura.
    // `grid-cols-[minmax(0,1fr)]`: sem coluna declarada, a coluna implícita crescia até a
    // largura mínima do conteúdo mais largo — o formulário de desfecho do detalhe — e o
    // painel inteiro passava da borda, cortado (25/09).
    <section
      className={`grid ${janelaNoCelular ? "max-h-[70dvh]" : ""} min-h-0 min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[auto_1fr_auto] overflow-hidden rounded-cartao border border-linha bg-painel md:max-h-none`}
    >
      <header className="flex items-center gap-2 border-b border-linha bg-painel-2 px-3 py-2">
        <h2 className="text-titulo-painel font-bold tracking-[0.08em] text-tinta uppercase">
          {titulo}
        </h2>
        <div className="ml-auto flex items-center gap-2">{acoes}</div>
      </header>
      {/* `auto-rows-max` junto de `content-start`: `content-start` sozinho **não** impede o
          Chrome de repartir a altura do painel igualmente entre as linhas `auto` do corpo —
          os cartões desta tela apareciam todos com a mesma altura, cada um cortando o que
          nele sobrava. Com as linhas em `max-content`, cada filho tem a altura que pede e
          quem rola é o corpo, que é o contrato deste componente. */}
      {/* `*:min-w-0`: item de grid tem `min-width: auto`, e um filho com largura fixa — o
          SVG do gráfico de barras, uma linha de texto sem quebra — puxava a **coluna** do
          painel para 452 px numa tela de 390, e o celular abria com zoom para fora
          (`C-17`). Com o mínimo em zero, quem estoura rola dentro do próprio filho. */}
      <div
        className={
          rolagemPropria
            ? `grid min-h-0 gap-2.5 p-3 *:min-w-0 ${
                janelaNoCelular
                  ? "grid-rows-[auto_minmax(0,1fr)] overflow-hidden"
                  : "auto-rows-max md:grid-rows-[auto_minmax(0,1fr)] md:overflow-hidden"
              }`
            : `grid min-h-0 auto-rows-max content-start gap-2.5 p-3 *:min-w-0 ${
                janelaNoCelular ? "overflow-y-auto" : "md:overflow-y-auto"
              }`
        }
      >
        {children}
      </div>
      {rodape !== undefined && (
        <footer className="flex items-center justify-between border-t border-linha bg-topo px-3 py-2 text-[11px] text-tinta-3">
          {rodape}
        </footer>
      )}
    </section>
  );
}

type Severidade = "neutro" | "positivo" | "atencao" | "critico";

/**
 * A borda e o fundo de cada severidade, **separados**: o cartão ativo troca os dois (`border-azul`
 * e `bg-realce`), e duas utilidades de fundo (ou de borda) no mesmo `className` não se resolvem
 * pela ordem em que foram escritas — vale a que vem depois no CSS gerado. Era assim que o filtro
 * crítico (o vermelho, justo o que o número do menu leva) não mudava de aparência ao ser aceso
 * (revisão geral de 30/10, `N240`). `tests/base.teste.tsx` recusa a volta.
 */
const BORDA_DA_SEVERIDADE: Record<Severidade, string> = {
  neutro: "border-linha",
  positivo: "border-verde-borda",
  atencao: "border-amarelo-borda",
  critico: "border-vermelho-borda",
};

const FUNDO_DA_SEVERIDADE: Record<Severidade, string> = {
  neutro: "bg-painel hover:bg-painel-2",
  positivo: "bg-painel hover:bg-painel-2",
  atencao: "bg-amarelo-fundo",
  critico: "bg-vermelho-fundo",
};

/** O aspecto do cartão ou do filtro: aceso, o azul; senão, o da severidade. */
function aspectoDoCartao(severidade: Severidade, ativo: boolean): string {
  return ativo
    ? "border-azul bg-realce"
    : `${BORDA_DA_SEVERIDADE[severidade]} ${FUNDO_DA_SEVERIDADE[severidade]}`;
}

const COR_DA_SEVERIDADE = {
  positivo: "verde",
  atencao: "amarelo",
  critico: "vermelho",
} as const;

const NUMERO_DA_SEVERIDADE: Record<Severidade, string> = {
  neutro: "text-tinta",
  positivo: "text-verde",
  atencao: "text-amarelo",
  critico: "text-vermelho",
};

/**
 * §3: ícone num quadrado tingido à esquerda, rótulo acima, número grande abaixo.
 *
 * **Sempre clicável e sempre filtra a tabela abaixo** (§4.1). Um número que não leva ao que
 * ele conta é um número que ninguém confere — e é o `AC04` do cronograma, que exige que
 * filtros e KPIs reflitam os mesmos registros.
 *
 * A severidade só acende com `valor > 0`: um cartão vermelho marcando zero treina o
 * operador a ignorar vermelho, que é o oposto do que a cor existe para fazer.
 */
export function Kpi({
  rotulo,
  valor,
  icone,
  severidade = "neutro",
  ativo = false,
  aoClicar,
  detalhe,
  compacto = false,
}: {
  rotulo: string;
  /** Uma linha pequena sob o número — o "próximo vence em 2d 4h" dos cartões de prazo. */
  detalhe?: string | undefined;
  /** A régua de uma linha só (Gisele, 25/09: "os big numbers têm que caber todos em uma
   *  linha, quebra os títulos"). Rótulo sem quebra, número menor e o ícone só em tela larga
   *  (2xl): num notebook de 1366 px são sete cartões de ~150 px, e ícone de 36 px mais
   *  "Aguardando retorno" não cabem juntos. */
  compacto?: boolean;
  /** `null` é *ainda não sei*, e sai como travessão. Zero é uma afirmação sobre a
   *  operação, e um zero mostrado enquanto o número carrega é a afirmação errada — logo
   *  num cartão que pode estar pintado de vermelho. */
  valor: number | null;
  icone: ReactNode;
  severidade?: Severidade;
  ativo?: boolean;
  aoClicar: () => void;
}) {
  // A cor só acende com número. Um cartão vermelho marcando zero (ou carregando) ensina a
  // ignorar vermelho, que é o oposto do que a cor existe para fazer.
  const acesa = valor !== null && valor > 0 ? severidade : "neutro";
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={aoClicar}
      // No celular (`C-17`) o ícone sobe e o número ganha a largura toda do cartão: a régua
      // fica em duas colunas de ~170 px, e "1.405.350" em 27 px mono não cabe ao lado de um
      // ícone de 36. Na mesa, o cartão é o de sempre.
      title={
        compacto ? [rotulo, detalhe].filter(Boolean).join(" — ") : undefined
      }
      className={`flex min-w-0 flex-col items-start gap-1.5 rounded-cartao border text-left transition-colors md:flex-row md:items-center ${
        compacto ? "px-2.5 py-2 md:gap-2" : "px-3 py-2.5 md:gap-2.5"
      } ${aspectoDoCartao(acesa, ativo)}`}
    >
      <span
        className={`size-8 shrink-0 place-items-center rounded-[9px] border md:size-9 ${
          compacto ? "grid md:hidden 2xl:grid" : "grid"
        } ${
          acesa === "neutro"
            ? "border-linha bg-painel-2 text-tinta-3"
            : TINTA[COR_DA_SEVERIDADE[acesa]]
        }`}
      >
        {icone}
      </span>
      <span
        className={`w-full min-w-0 ${compacto ? "md:flex-1" : "md:w-auto"}`}
      >
        <span
          className={`block text-[11.5px] leading-tight text-tinta-3 ${
            compacto ? "truncate md:whitespace-nowrap" : "line-clamp-2"
          }`}
        >
          {rotulo}
        </span>
        {/* O número leva a cor do que ele conta (21/09): "19.124" recusados em branco era
            lido como mais um número; em vermelho, é o problema da tela. */}
        <span
          className={`mono block text-[22px] leading-none font-extrabold ${
            compacto ? "mt-0.5 2xl:text-kpi" : "md:text-kpi"
          } ${NUMERO_DA_SEVERIDADE[acesa]}`}
        >
          {numeroLongo(valor)}
        </span>
        {detalhe && (
          <span
            className={`mt-1 block text-[10.5px] leading-tight text-tinta-3 ${
              compacto ? "truncate" : ""
            }`}
          >
            {detalhe}
          </span>
        )}
      </span>
    </button>
  );
}

/**
 * A régua compacta: o mesmo `Kpi` — rótulo, número, severidade, clique que filtra (§4.1) —
 * numa linha só, para a tela em que o número é contexto e a imagem é o trabalho.
 *
 * A Operação usa esta (21/09): com seis cartões altos em cima, a foto do veículo — que é o
 * que o operador confere — ficava com a metade de baixo da tela. As regras são as do
 * `Kpi`: a cor só acende com número, e travessão enquanto não chegou.
 *
 * `detalhe` é a linha de baixo do `Kpi` posta ao lado do número ("1º vence em 37m", 05/10).
 * Só aparece em tela larga — num notebook estreito os sete filtros quebrariam em duas
 * linhas —, e está sempre no `title`.
 */
export function FiltroContado({
  rotulo,
  valor,
  icone,
  severidade = "neutro",
  ativo = false,
  aoClicar,
  detalhe,
}: {
  rotulo: string;
  valor: number | null;
  icone: ReactNode;
  severidade?: Severidade;
  ativo?: boolean;
  aoClicar: () => void;
  detalhe?: string | undefined;
}) {
  const acesa = valor !== null && valor > 0 ? severidade : "neutro";
  const cor = acesa === "neutro" ? "text-tinta-3" : NUMERO_DA_SEVERIDADE[acesa];
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={aoClicar}
      title={detalhe ? `${rotulo} — ${detalhe}` : rotulo}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-pilula border px-2.5 py-1.5 text-left whitespace-nowrap transition-colors 2xl:gap-2 2xl:px-3 ${aspectoDoCartao(acesa, ativo)}`}
    >
      {/* O ícone só em tela larga (25/09): num notebook de 1366 px são sete filtros, e com
          ícone eles quebravam em duas linhas — a foto do veículo perdia a altura de uma. */}
      <span className={`hidden 2xl:inline ${cor}`}>{icone}</span>
      <span className="text-[11.5px] text-tinta-2">{rotulo}</span>
      <span
        className={`mono text-[15px] leading-none font-extrabold ${NUMERO_DA_SEVERIDADE[acesa]}`}
      >
        {numeroLongo(valor)}
      </span>
      {detalhe && (
        <span className="hidden text-[10.5px] text-tinta-3 xl:inline">
          {detalhe}
        </span>
      )}
    </button>
  );
}

/**
 * Os cinco estados que toda tela precisa ter (§5), mais o sexto que a `A-04` acrescentou.
 *
 * *Sem licença* é nosso e é frequente: 402 com `modulo_nao_licenciado`. Ele não pode virar
 * tabela vazia — a diferença entre "você não contratou isto" e "não há nada aqui" é a
 * diferença entre uma ligação para o comercial e uma para o suporte. `cota_esgotada` é
 * outro 402 e diz outra coisa (ADR-012); as duas mensagens nunca são a mesma.
 */
export function Estado({
  tipo,
  titulo,
  detalhe,
  acao,
}: {
  tipo: "carregando" | "vazio" | "erro" | "sem-licenca" | "bloqueado";
  titulo: string;
  detalhe?: string;
  acao?: ReactNode;
}) {
  const cor: Significado =
    tipo === "erro" || tipo === "bloqueado"
      ? "vermelho"
      : tipo === "sem-licenca"
        ? "amarelo"
        : "cinza";
  return (
    <div
      className="grid place-items-center gap-2 px-4 py-8 text-center"
      role="status"
    >
      <Badge cor={cor}>{ROTULO_DO_ESTADO[tipo]}</Badge>
      <p className="text-tinta-2">{titulo}</p>
      {detalhe && (
        <p className="max-w-prose text-[11.5px] text-tinta-3">{detalhe}</p>
      )}
      {acao}
    </div>
  );
}

const ROTULO_DO_ESTADO: Record<
  "carregando" | "vazio" | "erro" | "sem-licenca" | "bloqueado",
  string
> = {
  carregando: "carregando",
  vazio: "nada aqui",
  erro: "erro",
  "sem-licenca": "sem licença",
  bloqueado: "bloqueado",
};

/**
 * Botão. `papel` decide a forma, e a forma é normativa (§4).
 *
 * Os três papéis coloridos (21/09, pelo mockup da Gisele) são **decisões**, e a cor é a do
 * que a decisão faz com o trânsito: aprovar é verde (vai para a SENATRAN), mandar para o
 * backoffice é vermelho (sai da mão do operador), indicar revisão é âmbar (espera alguém).
 * Botão de navegação ou de ferramenta continua neutro — se tudo chama atenção, nada chama.
 */
const FORMA_DO_BOTAO = {
  primaria: "bg-azul-cheio text-white border-azul-cheio hover:bg-azul",
  secundaria: "bg-painel-2 text-tinta-2 border-linha hover:bg-realce",
  aprovar:
    "bg-verde-cheio text-white border-verde-cheio hover:bg-verde hover:text-fundo",
  perigo:
    "bg-vermelho-cheio text-white border-vermelho-cheio hover:bg-vermelho",
  atencao:
    "bg-amarelo-fundo text-amarelo border-amarelo-borda hover:bg-amarelo hover:text-fundo",
} as const;

export function Botao({
  papel = "secundaria",
  children,
  ...resto
}: {
  papel?: keyof typeof FORMA_DO_BOTAO;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const forma = FORMA_DO_BOTAO[papel];
  return (
    <button
      type="button"
      {...resto}
      className={`inline-flex min-h-8 items-center justify-center gap-1.5 rounded-botao border px-2.5 text-[11.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${forma}`}
    >
      {children}
    </button>
  );
}

/**
 * Um sub-cartão do painel de detalhe (§3).
 *
 * A grade é de **um assunto por cartão** — identificação, envio, retorno, arrecadação —
 * porque o operador procura por assunto e não por campo. Um cartão "Outros" com seis
 * campos soltos desfaz exatamente isso.
 */
export function Cartao({
  titulo,
  icone,
  children,
}: {
  titulo: string;
  icone?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="grid content-start gap-1.5 rounded-cartao border border-linha bg-painel-2 p-2.5">
      <h3 className="flex items-center gap-1.5 text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
        {icone}
        {titulo}
      </h3>
      {children}
    </section>
  );
}

/**
 * Um par rótulo/valor dentro de um cartão.
 *
 * O valor alinha à direita e usa `tabular-nums` quando é número: coluna de número que
 * dança é coluna que não se lê (§2).
 */
export function Linha({
  rotulo,
  children,
  mono = false,
}: {
  rotulo: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    // `flex-wrap` e `min-w-0`: num painel de 430px, `EM PROCESSAMENTO` ao lado de `Situação`
    // não cabe — e o que não cabe é cortado sem aviso. Quebrar para a linha de baixo perde
    // um pouco de densidade e não perde nenhum caractere.
    <div className="flex flex-wrap items-center justify-between gap-x-2 border-b border-linha-suave pb-1 last:border-0 last:pb-0">
      <span className="text-[11px] text-tinta-3">{rotulo}</span>
      <span className={`min-w-0 text-right text-tinta-2 ${mono ? "mono" : ""}`}>
        {children}
      </span>
    </div>
  );
}
